import type { ReactNode } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontFamily, radii } from '~/ui/tokens';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

type PageItem = number | 'ellipsis-start' | 'ellipsis-end';

function getPageItems(current: number, total: number): PageItem[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  if (current <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis-end', total];
  }

  if (current >= total - 3) {
    return [1, 'ellipsis-start', total - 4, total - 3, total - 2, total - 1, total];
  }

  return [1, 'ellipsis-start', current - 1, current, current + 1, 'ellipsis-end', total];
}

export function Pagination({ currentPage, totalPages, onPageChange, disabled = false }: PaginationProps) {
  if (totalPages <= 1) return null;

  const items = getPageItems(currentPage, totalPages);
  const canPrev = currentPage > 1 && !disabled;
  const canNext = currentPage < totalPages && !disabled;

  return (
    <View style={styles.container} accessibilityLabel="Pagination">
      {/* Previous Button [<] */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Previous page"
        accessibilityState={{ disabled: !canPrev }}
        disabled={!canPrev}
        onPress={() => onPageChange(currentPage - 1)}
        style={({ pressed }) => [
          styles.navButton,
          !canPrev && styles.navButtonDisabled,
          pressed && canPrev && styles.buttonPressed,
        ]}
      >
        <Ionicons name="chevron-back" size={16} color={canPrev ? colors.ink : colors.inkFaint} />
      </Pressable>

      {/* Page Numbers & Ellipses */}
      <View style={styles.pageList}>
        {items.map((item, idx) => {
          if (typeof item === 'string') {
            return (
              <View key={`ellipsis-${item}-${idx}`} style={styles.ellipsis}>
                <Text style={styles.ellipsisText}>…</Text>
              </View>
            );
          }

          const isActive = item === currentPage;

          return (
            <Pressable
              key={`page-${item}`}
              accessibilityRole="button"
              accessibilityLabel={`Page ${item}`}
              accessibilityState={{ selected: isActive, disabled }}
              disabled={disabled || isActive}
              onPress={() => onPageChange(item)}
              style={({ pressed }) => [
                styles.pageButton,
                isActive && styles.pageButtonActive,
                pressed && !isActive && styles.buttonPressed,
              ]}
            >
              <Text style={[styles.pageButtonText, isActive && styles.pageButtonTextActive]}>
                {item}
              </Text>
            </Pressable>
          );
        })}
      </View>

      {/* Next Button [>] */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Next page"
        accessibilityState={{ disabled: !canNext }}
        disabled={!canNext}
        onPress={() => onPageChange(currentPage + 1)}
        style={({ pressed }) => [
          styles.navButton,
          !canNext && styles.navButtonDisabled,
          pressed && canNext && styles.buttonPressed,
        ]}
      >
        <Ionicons name="chevron-forward" size={16} color={canNext ? colors.ink : colors.inkFaint} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 24,
  },
  pageList: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  navButton: {
    width: 38,
    height: 38,
    borderRadius: radii.md,
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navButtonDisabled: {
    opacity: 0.4,
    borderColor: colors.lineSoft,
    backgroundColor: colors.cream,
  },
  pageButton: {
    minWidth: 38,
    height: 38,
    paddingHorizontal: 8,
    borderRadius: radii.md,
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pageButtonActive: {
    backgroundColor: colors.brand,
    borderColor: colors.brand,
  },
  pageButtonText: {
    fontFamily: fontFamily.medium,
    fontSize: 14,
    color: colors.ink,
  },
  pageButtonTextActive: {
    fontFamily: fontFamily.bold,
    color: colors.white,
  },
  buttonPressed: {
    opacity: 0.7,
  },
  ellipsis: {
    minWidth: 24,
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ellipsisText: {
    fontFamily: fontFamily.medium,
    fontSize: 14,
    color: colors.inkFaint,
  },
});
